import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { JobServiceSubscriptionRequest_Target as SubscriptionTarget } from "protos/rtc/web_socket";

// Id bucket used by targets that have no entity id of their own (the global
// interventions feed)
export const GLOBAL_ID = "";

// The targets the UI can subscribe to. A subset of the proto target enum.
export type SubscribableTarget =
  | SubscriptionTarget.JOB
  | SubscriptionTarget.JOB_CONFIG
  | SubscriptionTarget.OBJECTIVE
  | SubscriptionTarget.TASK
  | SubscriptionTarget.ROBOT
  | SubscriptionTarget.INTERVENTIONS;

// A single subscription: a target plus its id ("" for global targets). The id
// is always a string here — numeric ids (job/objective/task) are stringified by
// the caller, and only collapse back to a number at the wire boundary.
export interface SubscriptionRef {
  target: SubscribableTarget;
  id: string;
}

// Counts nested by target then id, e.g. { [JOB]: { "1": 2 }, [ROBOT]: { "abc": 1 } }.
export type SubscriptionCounts = Record<
  SubscribableTarget,
  Record<string, number>
>;

interface JobWebsocketState {
  authenticated: boolean;
  subscriptions: SubscriptionCounts;
}

const emptySubscriptions = (): SubscriptionCounts => ({
  [SubscriptionTarget.JOB]: {},
  [SubscriptionTarget.JOB_CONFIG]: {},
  [SubscriptionTarget.OBJECTIVE]: {},
  [SubscriptionTarget.TASK]: {},
  [SubscriptionTarget.ROBOT]: {},
  [SubscriptionTarget.INTERVENTIONS]: {},
});

const initialState: JobWebsocketState = {
  authenticated: false,
  subscriptions: emptySubscriptions(),
};

function increment(
  state: JobWebsocketState,
  target: SubscribableTarget,
  id: string
): void {
  const bucket = state.subscriptions[target];
  bucket[id] = (bucket[id] ?? 0) + 1;
}

function decrement(
  state: JobWebsocketState,
  target: SubscribableTarget,
  id: string
): void {
  const bucket = state.subscriptions[target];
  const count = (bucket[id] ?? 0) - 1;
  if (count <= 0) {
    delete bucket[id];
  } else {
    bucket[id] = count;
  }
}

export const jobWebsocketSlice = createSlice({
  name: "jobWebsocket",
  initialState,
  reducers: {
    subscribe(state, { payload }: PayloadAction<SubscriptionRef>) {
      increment(state, payload.target, payload.id);
    },
    unsubscribe(state, { payload }: PayloadAction<SubscriptionRef>) {
      decrement(state, payload.target, payload.id);
    },
    setAuthenticated(state, { payload }: PayloadAction<boolean>) {
      state.authenticated = payload;
    },
  },
});

export type JobWebsocketRootState = { jobWebsocket: JobWebsocketState };

// Re-export the proto target so callers have one import site; this is the wire
// enum, not a parallel cloud-side type.
export { JobServiceSubscriptionRequest_Target as SubscriptionTarget } from "protos/rtc/web_socket";
